import { useState } from "react";
import { cn } from "@/lib/utils";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import { Search, Play, Download, RefreshCw, MoreHorizontal, Trash2, Eye, User, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, X } from "lucide-react";
import { useVideos } from "@/hooks/useVideos";
import { Skeleton } from "@/components/ui/skeleton";

export function VideosPage() {
  const { 
    videos, 
    stats, 
    loading, 
    error, 
    refresh, 
    page, 
    setPage, 
    total, 
    limit, 
    search, 
    setSearch, 
    status, 
    setStatus 
  } = useVideos();
  const [selectedVideo, setSelectedVideo] = useState<{ url: string; title: string } | null>(null);

  const getStatusBadge = (status: string) => {
    const styles = {
      completed: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100",
      processing: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100",
      pending: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-100",
      failed: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100",
    };
    return styles[status as keyof typeof styles] || "bg-gray-100 text-gray-800";
  };

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-[400px] gap-4">
        <p className="text-red-500">Error: {error}</p>
        <Button onClick={refresh} variant="outline">Retry</Button>
      </div>
    );
  }

  return (
    <TooltipProvider delayDuration={0}>
      <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Videos</h2>
          <p className="text-muted-foreground">
            Manage all video generation jobs across the platform
          </p>
        </div>
        <Button variant="outline" size="icon" onClick={refresh} title="Refresh Videos">
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="p-4">
            {loading && !stats ? (
              <div className="space-y-2">
                <Skeleton className="h-8 w-[60px]" />
                <Skeleton className="h-3 w-[80px]" />
              </div>
            ) : (
              <>
                <div className="text-2xl font-bold">{stats?.total || 0}</div>
                <p className="text-xs text-muted-foreground">Total Videos</p>
              </>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            {loading && !stats ? (
               <div className="space-y-2">
                <Skeleton className="h-8 w-[60px]" />
                <Skeleton className="h-3 w-[80px]" />
              </div>
            ) : (
              <>
                <div className="text-2xl font-bold text-green-600">{stats?.completed || 0}</div>
                <p className="text-xs text-muted-foreground">Completed</p>
              </>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            {loading && !stats ? (
               <div className="space-y-2">
                <Skeleton className="h-8 w-[60px]" />
                <Skeleton className="h-3 w-[80px]" />
              </div>
            ) : (
              <>
                <div className="text-2xl font-bold text-blue-600">{stats?.processing || 0}</div>
                <p className="text-xs text-muted-foreground">Processing</p>
              </>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            {loading && !stats ? (
               <div className="space-y-2">
                <Skeleton className="h-8 w-[60px]" />
                <Skeleton className="h-3 w-[80px]" />
              </div>
            ) : (
              <>
                <div className="text-2xl font-bold text-red-600">{stats?.failed || 0}</div>
                <p className="text-xs text-muted-foreground">Failed</p>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="p-4">
          {loading && !stats ? (
            <div className="flex gap-4">
              <Skeleton className="h-10 flex-1" />
              <div className="flex gap-2">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} className="h-9 w-20" />
                ))}
              </div>
            </div>
          ) : (
            <div className="flex gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search videos by title, user ID or email..."
                  className="pl-8"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <div className="flex gap-2">
                {["all", "completed", "processing", "pending", "failed"].map((s) => (
                  <Button
                    key={s}
                    variant={status === s ? "default" : "outline"}
                    size="sm"
                    onClick={() => setStatus(s)}
                  >
                    {s.charAt(0).toUpperCase() + s.slice(1)}
                  </Button>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Videos Table */}
      <Card>
        <CardHeader>
          <CardTitle>All Videos</CardTitle>
          <CardDescription>
            Showing {videos.length} of {total} videos
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Video</TableHead>
                <TableHead>User</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && videos.length === 0 ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <div className="space-y-1">
                        <Skeleton className="h-4 w-[200px]" />
                        <Skeleton className="h-3 w-[100px]" />
                      </div>
                    </TableCell>
                    <TableCell><Skeleton className="h-4 w-[150px]" /></TableCell>
                    <TableCell><Skeleton className="h-5 w-[80px] rounded-full" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-[80px]" /></TableCell>
                    <TableCell className="text-right"><Skeleton className="h-8 w-20 ml-auto rounded-md" /></TableCell>
                  </TableRow>
                ))
              ) : videos.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="h-24 text-center">
                    No videos found.
                  </TableCell>
                </TableRow>
              ) : (
                videos.map((video) => (
                  <TableRow key={video.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{video.title}</p>
                        <p className="text-xs text-muted-foreground">ID: {video.id}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-muted-foreground" />
                        <span className="truncate max-w-[200px]" title={video.userEmail || video.userId}>
                            {video.userEmail || video.userId}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {video.status === "failed" && video.errorMessage ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Badge variant="secondary" className={cn("cursor-help", getStatusBadge(video.status))}>
                              {video.status}
                            </Badge>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-[300px] wrap-break-word">
                            <p className="text-xs font-semibold mb-1">Error Message:</p>
                            <p>{video.errorMessage}</p>
                          </TooltipContent>
                        </Tooltip>
                      ) : (
                        <Badge variant="secondary" className={getStatusBadge(video.status)}>
                          {video.status}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>{new Date(video.createdAt).toLocaleDateString()} {new Date(video.createdAt).toLocaleTimeString()}</TableCell>
                    <TableCell className="text-right">
                      {video.status === "completed" && video.videoUrl ? (
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => setSelectedVideo({ url: video.videoUrl!, title: video.title })}
                        >
                          <Play className="mr-2 h-4 w-4" />
                          Watch
                        </Button>
                      ) : (
                        <Button variant="ghost" size="sm" disabled>
                          -
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={!!selectedVideo} onOpenChange={(open) => !open && setSelectedVideo(null)}>
        <DialogContent className="sm:max-w-[95vw] xl:max-w-[1200px] p-0 overflow-hidden bg-black border-none">
          <DialogHeader className="p-4 absolute top-0 left-0 right-0 z-10 bg-linear-to-b from-black/80 to-transparent pointer-events-none">
            <DialogTitle className="text-white flex justify-between items-center">
              <span className="truncate">{selectedVideo?.title}</span>
            </DialogTitle>
          </DialogHeader>
          <div className="relative pt-[56.25%] bg-black">
            {selectedVideo && (
              <video 
                src={selectedVideo.url} 
                controls 
                autoPlay 
                className="absolute inset-0 w-full h-full"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
      
       {/* Pagination */}
       <div className="flex items-center justify-end space-x-2 py-4">
        <div className="flex-1 text-sm text-muted-foreground">
          Showing {videos.length > 0 ? (page - 1) * limit + 1 : 0} to {Math.min(page * limit, total)} of {total} videos
        </div>
        <div className="space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(1)}
            disabled={page === 1 || loading}
          >
            <ChevronsLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1 || loading}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => p + 1)}
            disabled={page * limit >= total || loading}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
           <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(Math.ceil(total / limit))}
            disabled={page * limit >= total || loading}
          >
            <ChevronsRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
      </div>
    </TooltipProvider>
  );
}
